import { AddonSchedule, AddonScheduleMode } from 'types/AddonSchedule';
import { AddonService } from 'types/AddonService';
import {
    PortalAddonEntry,
    PortalBooking,
    PortalBookingAddon,
    PortalBookingAppointment,
    PortalBookingPet
} from 'types/PortalBooking';
import { addonFitsBusinessArea, getAddonDates, getScheduleOccurrences } from 'utils/addonSchedule';

// petId -> selection key (group + Add-On, see getSelectionKey) -> schedule. Only new, unsaved
// selections live here: what the booking already has is read from the booking itself.
export type AddonSelections = Record<number, Record<string, AddonSchedule>>;

// date -> quantity already saved on the booking for one pet and Add-On.
export type LockedDays = Record<string, number>;

/** The Add-Ons of one service of the pet, shown under "{Service} Add-ons". */
export interface AddonGroup {
    key: string;
    label: string;
    appointments: Array<PortalBookingAppointment>;
    addons: Array<AddonService>;
}

/** One new pet x Add-On selection, as the summary shows it. */
export interface AddonSelectionLine {
    key: string;
    selectionKey: string;
    pet: PortalBookingPet;
    addon: AddonService;
    schedule: AddonSchedule;
    // Days the schedule may use: eligible for the group and not already on the booking.
    dates: Array<string>;
    units: number;
    amount: number;
}

/** Lines already saved on the booking for one pet and Add-On, grouped. */
export interface SavedAddonLine {
    addonServiceId: number;
    name: string;
    dates: Array<string>;
    quantities: Array<number>;
    hasUndatedLine: boolean;
}

const roundMoney = (amount: number) => Math.round(amount * 100) / 100;

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

export const getSelectionKey = (groupKey: string, addonId: number) => `${groupKey}:${addonId}`;

// Pets with at least one day still open for Add-Ons, in booking order.
export const getEligiblePets = (booking: PortalBooking): Array<PortalBookingPet> => {
    const pets = new Map<number, PortalBookingPet>();

    booking.appointments
        .filter(appointment => appointment.addonDates.length > 0)
        .forEach(appointment => {
            if (!pets.has(appointment.pet.id)) {
                pets.set(appointment.pet.id, appointment.pet);
            }
        });

    return Array.from(pets.values());
};

export const getLockedDays = (
    booking: PortalBooking,
    petId: number,
    addonServiceId: number
): LockedDays =>
    booking.addons
        .filter(
            addon =>
                addon.pet.id === petId &&
                addon.addonServiceId === addonServiceId &&
                addon.date !== null
        )
        .reduce<LockedDays>(
            (days, addon) => ({
                ...days,
                [addon.date!]: (days[addon.date!] ?? 0) + addon.quantity
            }),
            {}
        );

/** Eligible days of the group for the Add-On, the ones already on the booking left out. */
export const getOpenDates = (
    addon: AddonService,
    appointments: Array<PortalBookingAppointment>,
    locked: LockedDays
) => getAddonDates(addon, appointments).filter(date => locked[date] === undefined);

/**
 * One group per service the pet has on the booking ("Daycare Add-ons", "Boarding Add-ons"), each
 * with the catalog Add-Ons that fit it and have at least one eligible day there. An Add-On with no
 * business area fits every group.
 */
export const getAddonGroups = (
    booking: PortalBooking,
    petId: number,
    catalog: Array<AddonService>
): Array<AddonGroup> => {
    const groups = new Map<string, AddonGroup>();

    booking.appointments
        .filter(appointment => appointment.pet.id === petId && appointment.addonDates.length > 0)
        .forEach(appointment => {
            const { businessArea, id, name } = appointment.service;
            const key = businessArea ?? `service-${id}`;
            const group = groups.get(key);

            if (group) {
                group.appointments.push(appointment);
            } else {
                groups.set(key, {
                    key,
                    label: `${businessArea ? capitalize(businessArea) : name} Add-ons`,
                    appointments: [appointment],
                    addons: []
                });
            }
        });

    return Array.from(groups.values())
        .map(group => ({
            ...group,
            addons: catalog.filter(
                addon =>
                    group.appointments.some(appointment =>
                        addonFitsBusinessArea(addon, appointment.service.businessArea)
                    ) && getAddonDates(addon, group.appointments).length > 0
            )
        }))
        .filter(group => group.addons.length > 0);
};

export const getScheduleUnitsOn = (schedule: AddonSchedule, dates: Array<string>) =>
    getScheduleOccurrences(schedule, dates).reduce(
        (units, occurrence) => units + occurrence.quantity,
        0
    );

/**
 * What a selection adds on top of the booking. Everyday covers the open days. In Select Days a
 * day already on the booking holds the quantity the pet parent wants in total, so only the
 * difference is new, and a day left at what is saved adds nothing.
 */
const getNewSchedule = (
    eligibleDates: Array<string>,
    locked: LockedDays,
    schedule: AddonSchedule
) => {
    const openDates = eligibleDates.filter(date => locked[date] === undefined);

    if (schedule.mode === AddonScheduleMode.Everyday) {
        return { dates: openDates, effective: schedule };
    }

    const days: Record<string, number> = {};

    eligibleDates.forEach(date => {
        const added = (schedule.days[date] ?? 0) - (locked[date] ?? 0);

        if (schedule.days[date] !== undefined && added > 0) {
            days[date] = added;
        }
    });

    return { dates: Object.keys(days).sort(), effective: { ...schedule, days } };
};

/** Every new selection with its open days and price (pet x Add-On x date x quantity). */
export const getSelectionLines = (
    booking: PortalBooking,
    catalog: Array<AddonService>,
    selections: AddonSelections
): Array<AddonSelectionLine> =>
    getEligiblePets(booking).flatMap(pet => {
        const groups = getAddonGroups(booking, pet.id, catalog);

        return Object.entries(selections[pet.id] ?? {}).flatMap(([key, schedule]) => {
            const [groupKey, addonId] = key.split(':');
            const group = groups.find(candidate => candidate.key === groupKey);
            const addon = group?.addons.find(candidate => candidate.id === Number(addonId));

            if (!group || !addon) {
                return [];
            }

            const locked = getLockedDays(booking, pet.id, addon.id);
            const { dates, effective } = getNewSchedule(
                getAddonDates(addon, group.appointments),
                locked,
                schedule
            );
            const units = getScheduleUnitsOn(effective, dates);

            return [
                {
                    key: `${pet.id}-${key}`,
                    selectionKey: key,
                    pet,
                    addon,
                    schedule: effective,
                    dates,
                    units,
                    amount: roundMoney(addon.price * units)
                }
            ];
        });
    });

export const getSelectionsTotal = (lines: Array<AddonSelectionLine>) =>
    roundMoney(lines.reduce((total, line) => total + line.amount, 0));

/**
 * The POST body lines: one per pet, Add-On and day, the quantities of the same day added up when
 * the Add-On was picked in two groups. A day already on the booking never goes out.
 * `uuidFor` gives the line its idempotency key.
 */
export const buildAddonEntries = (
    lines: Array<AddonSelectionLine>,
    uuidFor: (key: string) => string
): Array<PortalAddonEntry> => {
    const quantities = new Map<
        string,
        { petId: number; addonServiceId: number; date: string; quantity: number }
    >();

    lines.forEach(line =>
        getScheduleOccurrences(line.schedule, line.dates).forEach(occurrence => {
            const key = `${line.pet.id}|${line.addon.id}|${occurrence.date}`;
            const current = quantities.get(key);

            quantities.set(key, {
                petId: line.pet.id,
                addonServiceId: line.addon.id,
                date: occurrence.date,
                quantity: (current?.quantity ?? 0) + occurrence.quantity
            });
        })
    );

    return Array.from(quantities.entries()).map(([key, entry]) => ({
        // The quantity is part of the key: a retry of the same line reuses its uuid, a changed
        // quantity is a different line.
        uuid: uuidFor(`${key}|${entry.quantity}`),
        ...entry
    }));
};

/** Selections without the days the booking now has, and without schedules left with no day. */
export const dropLockedDays = (
    booking: PortalBooking,
    selections: AddonSelections
): { selections: AddonSelections; changed: boolean } => {
    let changed = false;
    const next: AddonSelections = {};

    Object.entries(selections).forEach(([petId, petSelections]) => {
        next[Number(petId)] = {};

        Object.entries(petSelections).forEach(([key, schedule]) => {
            const addonId = Number(key.split(':')[1]);
            const locked = getLockedDays(booking, Number(petId), addonId);
            const days = { ...schedule.days };

            Object.keys(days)
                .filter(date => locked[date] !== undefined && days[date] <= locked[date])
                .forEach(date => {
                    delete days[date];
                    changed = true;
                });

            if (schedule.mode === AddonScheduleMode.SelectDays && Object.keys(days).length === 0) {
                changed = changed || Object.keys(schedule.days).length > 0;
                return;
            }

            next[Number(petId)][key] = { ...schedule, days };
        });
    });

    return { selections: next, changed };
};

/**
 * Removes the entries the backend rejected (`addons_not_available` answers their positions in
 * the request) and keeps everything else as it was.
 */
export const dropUnavailableEntries = (
    selections: AddonSelections,
    lines: Array<AddonSelectionLine>,
    rejected: Array<PortalAddonEntry>
): AddonSelections => {
    const next: AddonSelections = JSON.parse(JSON.stringify(selections));

    rejected.forEach(entry => {
        lines
            .filter(line => line.pet.id === entry.petId && line.addon.id === entry.addonServiceId)
            .forEach(line => {
                const key = line.selectionKey;
                const schedule = next[line.pet.id]?.[key];

                if (!schedule) {
                    return;
                }

                if (schedule.mode === AddonScheduleMode.Everyday) {
                    // Everyday becomes the open days minus the rejected one.
                    schedule.mode = AddonScheduleMode.SelectDays;
                    schedule.days = Object.fromEntries(
                        line.dates.map(date => [date, schedule.quantity])
                    );
                }

                delete schedule.days[entry.date];

                if (Object.keys(schedule.days).length === 0) {
                    delete next[line.pet.id][key];
                }
            });
    });

    return next;
};

export const groupSavedAddons = (addons: Array<PortalBookingAddon>): Array<SavedAddonLine> => {
    const lines = new Map<number, SavedAddonLine>();

    addons.forEach(addon => {
        const line = lines.get(addon.addonServiceId) ?? {
            addonServiceId: addon.addonServiceId,
            name: addon.name,
            dates: [],
            quantities: [],
            hasUndatedLine: false
        };

        if (addon.date === null) {
            line.hasUndatedLine = true;
            line.quantities.push(addon.quantity);
        } else {
            const index = line.dates.indexOf(addon.date);

            if (index >= 0) {
                line.quantities[index] += addon.quantity;
            } else {
                line.dates.push(addon.date);
                line.quantities.push(addon.quantity);
            }
        }

        lines.set(addon.addonServiceId, line);
    });

    return Array.from(lines.values()).map(line => {
        const order = line.dates
            .map((date, index) => ({ date, quantity: line.quantities[index] }))
            .sort((a, b) => a.date.localeCompare(b.date));
        const undated = line.quantities.slice(line.dates.length);

        return {
            ...line,
            dates: order.map(item => item.date),
            quantities: [...order.map(item => item.quantity), ...undated]
        };
    });
};

// YYYY-MM-DD (business time zone) read as a local calendar day, so the browser zone never moves it.
const toLocalDate = (date: string) => {
    const [year, month, day] = date.split('-').map(Number);

    return new Date(year, month - 1, day);
};

/** "Mon, May 12" */
export const formatShortDate = (date: string) =>
    toLocalDate(date).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
    });

/** "Monday, May 12" */
export const formatLongDate = (date: string) =>
    toLocalDate(date).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
    });

export const formatDateRange = (dates: Array<string>) =>
    dates.length > 1
        ? `${formatShortDate(dates[0])} – ${formatShortDate(dates[dates.length - 1])}`
        : formatShortDate(dates[0]);

export const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
    `${count} ${count === 1 ? singular : plural}`;

/** "Everyday" / "N selected day(s)", as Figma labels an Add-On in the summary. */
export const describeScheduleOn = (schedule: AddonSchedule, dates: Array<string>) => {
    if (schedule.mode === AddonScheduleMode.Everyday && dates.length > 1) {
        return 'Everyday';
    }

    return pluralize(getScheduleOccurrences(schedule, dates).length, 'selected day');
};

export const describeSchedule = (line: AddonSelectionLine) =>
    describeScheduleOn(line.schedule, line.dates);

/** "Mon, May 12" / "Mon, May 12 and Tue, May 13" */
export const joinDates = (dates: Array<string>) => dates.map(formatShortDate).join(' and ');
